/**
 * agent-approvals hooks (v1.1-C1) — full real-data binding for the
 * /approvals UI. Replaces every MOCK_PENDING_APPROVALS /
 * MOCK_HISTORY_APPROVALS read in the inbox, history, and detail
 * pages.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  ApprovalRequest,
  ApprovalAgent,
  ApprovalEvidence,
  ApprovalLevel,
  ApprovalPriority,
  ApprovalStatus,
  ApprovalType,
  HistoryEvent,
  ReasoningStep,
} from './mock-approvals-data';

// ─── Server VM (mirrors agent-approvals.ts ApprovalRowSchema) ─────

export interface ServerApprovalRow {
  id: string;
  agentId: string;
  agentName: string | null;
  agentVersionId: string;
  invocationId: string;
  toolName: string;
  summary: string;
  status: string;
  policyKind: string;
  expiresAt: string;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionRationale: string | null;
  requestedBy: string;
  requestedByDisplay: string | null;
  displayType: string | null;
  level: string | null;
  priority: string | null;
  riskScore: number | null;
  actionTitle: string | null;
  actionDescription: string | null;
  evidence: Record<string, unknown>;
  reasoningChain: unknown[];
  confidence: number | null;
  contextHistory: unknown[];
  contextSummary: unknown[];
  requiredApprovals: number;
  approvalsReceived: unknown[];
  // Sprint 2 — Auto-rendered Approval Cards (added Sprint 1 columns).
  // OPTIONAL because the API may not surface them yet; the auto-renderer
  // gates on presence. See packages/shared/src/schemas/approval-content-schema.ts.
  contentSchemaJsonb?: unknown;
  decidedEvidence?: Record<string, unknown> | null;
}

interface ListResponse {
  approvals: ServerApprovalRow[];
  total: number;
}

// ─── Adapter: server row → mock ApprovalRequest VM ────────────────

const STATUS_TO_VM: Record<string, ApprovalStatus> = {
  pending: 'pending',
  approved: 'approved',
  denied: 'rejected',
  expired: 'expired',
  auto_approved: 'approved',
};

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}
function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}
function asStringArray(v: unknown): string[] | undefined {
  return Array.isArray(v) && v.every((x) => typeof x === 'string')
    ? (v as string[])
    : undefined;
}

export function adaptServerApproval(s: ServerApprovalRow): ApprovalRequest {
  const ev = s.evidence ?? {};
  const agent: ApprovalAgent = {
    id: s.agentId,
    name: s.agentName ?? 'Argus Agent',
    icon: 'bot',
    color: '#1890FF',
  };

  const evidence: ApprovalEvidence = {
    conversationTitle: asString(ev.conversationTitle),
    userQuery: asString(ev.userQuery),
    parameters: (ev.parameters as Record<string, unknown> | undefined) ?? undefined,
    preview: asString(ev.preview),
    diff: ev.diff as ApprovalEvidence['diff'],
    relatedRecords: ev.relatedRecords as ApprovalEvidence['relatedRecords'],
    riskFactors: asStringArray(ev.riskFactors),
    recipients: asStringArray(ev.recipients),
    subject: asString(ev.subject),
    reasoningChain: asArray<ReasoningStep>(s.reasoningChain),
    confidence: s.confidence ?? undefined,
    alternativesConsidered: asStringArray(ev.alternativesConsidered),
    sourcesCited: asStringArray(ev.sourcesCited),
    contextHistory: asArray<HistoryEvent>(s.contextHistory),
    contextSummary: asArray<{ label: string; value: string }>(s.contextSummary),
  };

  return {
    id: s.id,
    type: (s.displayType as ApprovalType) ?? 'tool_call',
    agent,
    toolName: s.toolName,
    toolDisplayName: s.toolName.replace(/_/g, ' ').replace(/\./g, ' › '),
    action: {
      title: s.actionTitle ?? s.summary ?? 'Untitled action',
      description: s.actionDescription ?? '',
    },
    evidence,
    level: (s.level as ApprovalLevel) ?? 'single',
    status: STATUS_TO_VM[s.status] ?? 'pending',
    priority: (s.priority as ApprovalPriority) ?? 'medium',
    riskScore: s.riskScore ?? 0,
    requestedBy: s.requestedByDisplay ?? s.requestedBy,
    requestedAt: s.createdAt,
    expiresAt: s.expiresAt,
    decidedBy: s.decidedBy ?? undefined,
    decidedAt: s.decidedAt ?? undefined,
    decisionReason: s.decisionRationale ?? undefined,
    approvals: asArray<{ userId: string; approvedAt: string }>(
      s.approvalsReceived,
    ),
    requiredApprovals: s.requiredApprovals,
  };
}

// ─── Stats hook (R3 P1 #3 fix-automate-r3-p1) ──────────────────────
//
// Replaces the `|| 3` / `|| 1` / `'18m'` literal fallbacks on the
// Approvals Inbox stats tiles. The /approvals/stats endpoint now
// returns both lifetime and today-scoped counts; the UI tiles read
// the today variants so the page no longer fabricates numbers when
// the queue is empty.

export interface ApprovalStats {
  pending: number;
  approved: number;
  rejected: number;
  edited: number;
  avgDecisionMs: number;
  approvedToday: number;
  rejectedToday: number;
  editedToday: number;
  avgDecisionMsToday: number;
}

export function useApprovalStats() {
  return useQuery<ApprovalStats>({
    queryKey: ['approvals', 'stats'],
    queryFn: () => apiClient.get<ApprovalStats>('/approvals/stats'),
  });
}

// ─── List hooks ───────────────────────────────────────────────────

export function usePendingApprovals() {
  return useQuery<ListResponse, Error, ApprovalRequest[]>({
    queryKey: ['agent-approvals', 'pending'],
    queryFn: () =>
      apiClient.get<ListResponse>('/agent-approvals?status=pending'),
    select: (r) => r.approvals.map(adaptServerApproval),
  });
}

export function useHistoryApprovals() {
  return useQuery<ListResponse, Error, ApprovalRequest[]>({
    queryKey: ['agent-approvals', 'history'],
    queryFn: () =>
      apiClient.get<ListResponse>('/agent-approvals?status=history'),
    select: (r) => r.approvals.map(adaptServerApproval),
  });
}

export function useApproval(id: string | undefined) {
  return useQuery<ServerApprovalRow, Error, ApprovalRequest | null>({
    queryKey: ['agent-approvals', 'detail', id],
    enabled: !!id,
    queryFn: () => apiClient.get<ServerApprovalRow>(`/agent-approvals/${id}`),
    select: (r) => (r ? adaptServerApproval(r) : null),
  });
}

/**
 * Sprint 2 — returns the raw `ServerApprovalRow` (no VM adaptation) so the
 * auto-rendered approval card can read `contentSchemaJsonb` + `decidedEvidence`
 * directly. Reuses the same query key so it doesn't fire an extra network
 * request — TanStack Query dedupes via the cache.
 */
export function useApprovalRaw(id: string | undefined) {
  return useQuery<ServerApprovalRow, Error>({
    queryKey: ['agent-approvals', 'detail', id],
    enabled: !!id,
    queryFn: () => apiClient.get<ServerApprovalRow>(`/agent-approvals/${id}`),
  });
}

// ─── Decision mutations ───────────────────────────────────────────

export function useApproveDecision() {
  const qc = useQueryClient();
  return useMutation<
    ServerApprovalRow,
    Error,
    { id: string; rationale?: string }
  >({
    mutationFn: ({ id, rationale }) =>
      apiClient.post<ServerApprovalRow>(`/agent-approvals/${id}/approve`, {
        rationale: rationale ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-approvals'] });
    },
  });
}

export function useRejectDecision() {
  const qc = useQueryClient();
  return useMutation<
    ServerApprovalRow,
    Error,
    { id: string; rationale: string; category?: string }
  >({
    mutationFn: ({ id, rationale, category }) =>
      apiClient.post<ServerApprovalRow>(`/agent-approvals/${id}/reject`, {
        rationale,
        category,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-approvals'] });
    },
  });
}

// ─── Atomic bulk decision (feature-build #6) ──────────────────────
//
// Spec §14.1 calls for a single server-side batch endpoint rather than
// N client-side /approve round-trips. The backend POST /agent-approvals/
// /bulk re-checks the self-approval / two-person / level gates per item
// server-side and returns a per-row result envelope (so partial failures
// are still surfaced honestly). This hook replaces executeBulkApprovals'
// Promise.allSettled fan-out with the single batch call.

export interface BulkDecisionResultRow {
  id: string;
  status: string | null;
  error: { code: string; message: string } | null;
}

export interface BulkDecisionResponse {
  results: BulkDecisionResultRow[];
  successCount: number;
  failureCount: number;
}

export interface BulkDecisionRequest {
  approvalIds: string[];
  action: 'approve' | 'reject' | 'snooze' | 'dismiss';
  reason?: string;
  snoozeUntil?: string;
}

export function useBulkDecision() {
  const qc = useQueryClient();
  return useMutation<BulkDecisionResponse, Error, BulkDecisionRequest>({
    mutationFn: (body) =>
      apiClient.post<BulkDecisionResponse>('/agent-approvals/bulk', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-approvals'] });
      qc.invalidateQueries({ queryKey: ['approvals', 'stats'] });
    },
  });
}

export function useEditApproveDecision() {
  const qc = useQueryClient();
  return useMutation<
    ServerApprovalRow,
    Error,
    {
      id: string;
      overrideArgs: Record<string, unknown>;
      editedRecipients?: string[] | null;
      editedSubject?: string | null;
      editedBody?: string | null;
      rationale?: string | null;
    }
  >({
    mutationFn: ({ id, ...rest }) =>
      apiClient.post<ServerApprovalRow>(
        `/agent-approvals/${id}/edit-approve`,
        rest,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-approvals'] });
    },
  });
}

// ─── Settings ─────────────────────────────────────────────────────

export interface ApprovalSettingsVM {
  slaWindows: Record<string, unknown>;
  escalationRules: unknown[];
  delegation: Record<string, unknown>;
  notifications: Record<string, unknown>;
  routingRules: unknown[];
  progressiveAutonomy: Record<string, unknown>;
  updatedAt: string;
  /**
   * Bug #1103 + #1104 — caller's edit permission. False for non-
   * admin/owner callers; the UI MUST render policy controls
   * read-only when this is false rather than letting the user
   * click Save and discover the 403 after the fact. Defaults to
   * false when the backend response omits it (older deploys), so
   * the UI fails closed.
   */
  canUpdate?: boolean;
}

export function useApprovalSettings() {
  return useQuery<ApprovalSettingsVM, Error>({
    queryKey: ['approval-settings'],
    queryFn: () => apiClient.get<ApprovalSettingsVM>('/approval-settings'),
  });
}

export function useSaveApprovalSettings() {
  const qc = useQueryClient();
  return useMutation<ApprovalSettingsVM, Error, Partial<ApprovalSettingsVM>>({
    mutationFn: (body) =>
      apiClient.put<ApprovalSettingsVM>('/approval-settings', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-settings'] });
    },
  });
}
